import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Timer, Flame, TrendingUp, Calendar, Play, Square } from 'lucide-react';
import axios from 'axios';
import { API, getLocalISOString } from '../App';
import { toast } from 'sonner';

const IntermittentFasting = () => {
  const [currentFast, setCurrentFast] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedWindow, setSelectedWindow] = useState('16:8');
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [loading, setLoading] = useState(true);

  const fastingWindows = [
    { name: '16:8', fasting: 16, eating: 8, description: 'Most popular' },
    { name: '18:6', fasting: 18, eating: 6, description: 'Advanced' },
    { name: '20:4', fasting: 20, eating: 4, description: 'Warrior diet' },
    { name: '14:10', fasting: 14, eating: 10, description: 'Beginner friendly' },
  ];

  useEffect(() => {
    loadFastingData();
  }, []);

  useEffect(() => {
    let interval;
    if (currentFast && !currentFast.completed) {
      interval = setInterval(() => {
        calculateTimeRemaining();
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentFast]);

  const loadFastingData = async () => {
    setLoading(true);
    try {
      // Load current fast
      const currentResponse = await axios.get(`${API}/fasting/current`);
      setCurrentFast(currentResponse.data);

      // Load stats
      const statsResponse = await axios.get(`${API}/fasting/stats`);
      setStats(statsResponse.data);

      // Load history
      const historyResponse = await axios.get(`${API}/fasting/history?limit=10`);
      setHistory(historyResponse.data);
    } catch (error) {
      console.error('Error loading fasting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeRemaining = () => {
    if (!currentFast || currentFast.completed) {
      setTimeRemaining(null);
      return;
    }

    const startTime = new Date(currentFast.start_time);
    const now = new Date();
    const fastingHours = parseInt(currentFast.fasting_window.split(':')[0]);
    const endTime = new Date(startTime.getTime() + fastingHours * 60 * 60 * 1000);

    const remaining = endTime - now;

    if (remaining <= 0) {
      setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, completed: true });
    } else {
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
      setTimeRemaining({ hours, minutes, seconds, completed: false });
    }
  };

  const handleStartFast = async () => {
    try {
      await axios.post(`${API}/fasting/start`, {
        start_time: getLocalISOString(),
        fasting_window: selectedWindow
      });
      toast.success(`${selectedWindow} fast started!`);
      loadFastingData();
    } catch (error) {
      toast.error('Failed to start fast');
    }
  };

  const handleEndFast = async () => {
    if (!currentFast) return;

    try {
      await axios.post(`${API}/fasting/end/${currentFast.id}`, {
        end_time: getLocalISOString()
      });
      toast.success('Fast completed!');
      loadFastingData();
    } catch (error) {
      toast.error('Failed to end fast');
    }
  };

  const formatDuration = (hours) => {
    if (!hours) return '0h 0m';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  if (loading) {
    return (
      <div className="text-white text-center py-8">Loading fasting data...</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Fast Timer */}
      {currentFast && !currentFast.completed ? (
        <Card className="glass p-8 bg-gradient-to-br from-orange-500/20 to-red-500/20 border-2 border-orange-400">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Flame className="w-8 h-8 text-orange-400 animate-pulse" />
              <h2 className="text-3xl font-bold text-white">Fasting Active</h2>
              <Flame className="w-8 h-8 text-orange-400 animate-pulse" />
            </div>
            
            <p className="text-orange-200 mb-2">{currentFast.fasting_window} Window</p>
            
            {timeRemaining && !timeRemaining.completed ? (
              <div className="mb-6">
                <div className="text-6xl font-bold text-white mb-2">
                  {String(timeRemaining.hours).padStart(2, '0')}:
                  {String(timeRemaining.minutes).padStart(2, '0')}:
                  {String(timeRemaining.seconds).padStart(2, '0')}
                </div>
                <p className="text-orange-200 text-lg">Time Remaining</p>
              </div>
            ) : timeRemaining?.completed ? (
              <div className="mb-6">
                <p className="text-4xl font-bold text-emerald-400 mb-2">🎉 Fast Complete!</p>
                <p className="text-orange-200">You can break your fast now</p>
              </div>
            ) : null}

            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleEndFast}
                className="bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white font-semibold px-8 py-3 rounded-xl"
              >
                <Square className="w-4 h-4 mr-2" />
                End Fast
              </Button>
            </div>

            <p className="text-orange-300 text-sm mt-4">
              Started: {new Date(currentFast.start_time).toLocaleString()}
            </p>
          </div>
        </Card>
      ) : (
        /* Start Fast Controls */
        <Card className="glass p-8">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Timer className="w-6 h-6 mr-3 text-orange-400" />
            Start Intermittent Fast
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {fastingWindows.map((window) => (
              <button
                key={window.name}
                onClick={() => setSelectedWindow(window.name)}
                className={`p-4 rounded-xl transition-all ${
                  selectedWindow === window.name
                    ? 'bg-gradient-to-br from-orange-500 to-red-600 text-white ring-2 ring-orange-400'
                    : 'glass text-blue-300 hover:bg-blue-500/20'
                }`}
              >
                <p className="font-bold text-lg">{window.name}</p>
                <p className="text-xs mt-1 opacity-90">{window.description}</p>
              </button>
            ))}
          </div>

          <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-4 mb-6">
            <p className="text-blue-300 text-sm">
              <strong>{selectedWindow}</strong> means you'll fast for {fastingWindows.find(w => w.name === selectedWindow)?.fasting} hours 
              and have an eating window of {fastingWindows.find(w => w.name === selectedWindow)?.eating} hours.
            </p>
          </div>

          <Button
            onClick={handleStartFast}
            className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold py-4 rounded-xl text-lg"
          >
            <Play className="w-5 h-5 mr-2" />
            Start {selectedWindow} Fast
          </Button>
        </Card>
      )}

      {/* Stats */}
      {stats && (
        <Card className="glass p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-emerald-400" />
            Your Fasting Stats
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-white">{stats.current_streak}</p>
              <p className="text-blue-300 text-sm mt-1">Day Streak</p>
            </div>
            <div className="glass p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-white">{stats.total_fasts}</p>
              <p className="text-blue-300 text-sm mt-1">Total Fasts</p>
            </div>
            <div className="glass p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-white">{stats.avg_duration_hours}h</p>
              <p className="text-blue-300 text-sm mt-1">Avg Duration</p>
            </div>
            <div className="glass p-4 rounded-lg text-center">
              <p className="text-3xl font-bold text-white">{stats.total_hours_fasted}h</p>
              <p className="text-blue-300 text-sm mt-1">Total Hours</p>
            </div>
          </div>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="glass p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-blue-400" />
            Recent Fasts
          </h3>
          <div className="space-y-3">
            {history.slice(0, 5).map((fast) => (
              <div key={fast.id} className="glass p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white font-semibold">{fast.fasting_window}</p>
                    <p className="text-blue-300 text-sm">
                      {new Date(fast.start_time).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    {fast.completed ? (
                      <>
                        <p className="text-emerald-400 font-semibold">
                          {formatDuration(fast.duration_hours)}
                        </p>
                        <p className="text-emerald-300 text-xs">✓ Completed</p>
                      </>
                    ) : (
                      <p className="text-yellow-400 text-sm">In Progress</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default IntermittentFasting;
